/**
 * `<video [src]>` only ever plays a **direct media file** (`.mp4`, `.webm`, …). Admins paste the
 * share/watch link they copy from the browser instead, and that hands the element an HTML page:
 * the player renders but stays black at 0:00, both in the console preview and in the real modal.
 *
 * Recognised share links are translated here into their provider embed URL so the modal can render
 * an `<iframe>` for them. Anything unrecognised returns `null` and keeps going through `<video>`,
 * which is still the right element for a direct file URL.
 */

/** Hosts recognised as share links, already stripped of any `www.`/`m.` prefix. */
export enum VideoHost {
  youtuBe = 'youtu.be',
  youtube = 'youtube.com',
  youtubeNoCookie = 'youtube-nocookie.com',
  vimeo = 'vimeo.com',
  vimeoPlayer = 'player.vimeo.com',
}

/** First path segment of a YouTube link — decides where the video id is read from. */
export enum YoutubePath {
  watch = 'watch',
  embed = 'embed',
  shorts = 'shorts',
  live = 'live',
}

/** `player.vimeo.com/video/<id>` carries this segment; bare `vimeo.com/<id>` does not. */
const VIMEO_VIDEO_SEGMENT = 'video';

/** Query param carrying the video id on a `youtube.com/watch` link. */
const YOUTUBE_WATCH_ID_PARAM = 'v';

/** Embed templates — the id is substituted in, so a provider URL change is a one-line edit here. */
const EMBED_ID_PLACEHOLDER = '{id}';
const YOUTUBE_EMBED_URL = `https://www.youtube.com/embed/${EMBED_ID_PLACEHOLDER}`;
const VIMEO_EMBED_URL = `https://player.vimeo.com/video/${EMBED_ID_PLACEHOLDER}`;

const HTTPS_PROTOCOL = 'https:';
const HOST_PREFIX_PATTERN = /^(www\.|m\.)/;

/** YouTube ids are 11 url-safe chars today; the length is not contractual, the alphabet is. */
const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{6,}$/;
const VIMEO_ID_PATTERN = /^\d+$/;

export function toVideoEmbedUrl(rawUrl: string | undefined | null): string | null {
  const raw = (rawUrl ?? '').trim();
  if (!raw) return null;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  // Parse the host out of a real URL rather than matching the raw string: `https://evil.com/youtube.com/watch?v=x`
  // must not be treated as YouTube. The form validator already demands https, but this is what
  // actually reaches the DOM, so it re-checks.
  if (url.protocol !== HTTPS_PROTOCOL) return null;

  const host = url.hostname.toLowerCase().replace(HOST_PREFIX_PATTERN, '');
  const segments = url.pathname.split('/').filter(Boolean);

  if (host === VideoHost.youtuBe) return youtubeEmbed(segments[0]);

  if (host === VideoHost.youtube || host === VideoHost.youtubeNoCookie) {
    if (segments[0] === YoutubePath.watch) {
      return youtubeEmbed(url.searchParams.get(YOUTUBE_WATCH_ID_PARAM));
    }
    // `embed` is already the embeddable form; `shorts` and `live` are not, but carry the id the same way.
    if (
      segments[0] === YoutubePath.embed ||
      segments[0] === YoutubePath.shorts ||
      segments[0] === YoutubePath.live
    ) {
      return youtubeEmbed(segments[1]);
    }
    return null;
  }

  if (host === VideoHost.vimeo || host === VideoHost.vimeoPlayer) {
    // `vimeo.com/<id>` and `player.vimeo.com/video/<id>` both end in the numeric id.
    const id = segments[0] === VIMEO_VIDEO_SEGMENT ? segments[1] : segments[0];
    return embedUrl(VIMEO_EMBED_URL, id, VIMEO_ID_PATTERN);
  }

  return null;
}

function youtubeEmbed(id: string | null | undefined): string | null {
  return embedUrl(YOUTUBE_EMBED_URL, id, YOUTUBE_ID_PATTERN);
}

function embedUrl(template: string, id: string | null | undefined, pattern: RegExp): string | null {
  return id && pattern.test(id) ? template.replace(EMBED_ID_PLACEHOLDER, id) : null;
}
